import { isEqual, pick, range } from 'lodash';
import type { Answer, Poll, Question } from 'schema/poll';
import { generateScreens } from 'schema/src/kinds/poll/questions/MaxDiffPoll/generator/rnd';
import type { AnswerState, BackAnswerState, ICState, TrackingState } from 'schema/state';
import { v4 as uuid } from 'uuid';
import { nanoid } from '../polyfills';
import { getQuestionGroupId, selectQuestion, selectQuestionAnswers } from '../selects';
import {
  doFixedRandomizeAnswers,
  doRandomizeAnswers,
  findLastInStackSafe,
  getGroupQuestionsInStateIds,
  getLogicConditionContext,
  getTimerDistance,
  isRulesApply,
  LogicConditionContext,
  notNull,
  shuffleAnswersWithExcluded,
} from '../utils';

const MEDIAFILE_TYPES = [
  'MEDIA_FILE_IMAGE',
  'MEDIA_FILE_AUDIO',
  'MEDIA_FILE_VIDEO',
  'MEDIA_FILE_TEXT',
  'MEDIA_FILE_ANY',
] as const;

export function createDefaultTrackingState(type: TrackingState['type'], trackingId: string): TrackingState {
  return {
    id: trackingId,
    type,
    tracking: [],
  };
}

function findResolvedLink(links: string[], answersStack: ICState['answersStack']) {
  for (const link of links) {
    const state = findLastInStackSafe(answersStack, link);
    if (state) return state;
  }

  return null;
}

function createAnswers({ data, question, state }: { data: Poll; question: Question; state: ICState }) {
  const { answersStack, groupId } = state;
  const linkedQuestions = (state?.links ?? {})[question.id];
  const resolvedLink = linkedQuestions ? findResolvedLink(linkedQuestions, answersStack) : null;

  if (resolvedLink) {
    return { answersIds: resolvedLink.answersIds ?? [], answersOrder: resolvedLink.answersOrder ?? [] };
  }

  if (!('answers' in question.data && question.data.answers)) {
    return { answersIds: [], answersOrder: [] };
  }

  const { answers: originalAnswers } = question.data;

  const answersLimit: null | number =
    'answersLimit' in question.data && typeof question.data.answersLimit === 'number'
      ? question.data.answersLimit
      : null;

  /** У всех вопросов в матрице должны скрываться одинаковые ответы */
  const questionGroupId = getQuestionGroupId(question, data.questionDecks);

  const key =
    questionGroupId && ['MATRIX_POLL_SINGLE', 'MATRIX_POLL_MULTIPLE'].includes(question.data.type)
      ? questionGroupId
      : question.id;

  const { randomizeAnswers, randomizeAnswersExcludedIds } = question.data as {
    randomizeAnswers: boolean;
    randomizeAnswersExcludedIds?: string[];
  };

  const context = getLogicConditionContext(answersStack, data.questions, state.externalLogicRules);
  const getHiddenAnswers = (answers: Answer[], randomizeAnswers: boolean) =>
    computeHiddenAnswers(key, answers, { answersLimit, randomizeAnswers, randomizeAnswersExcludedIds }, context);

  const { fixedRandomizeAnswers } = question.data as { fixedRandomizeAnswers: { questionId: string } };
  if (fixedRandomizeAnswers?.questionId) {
    const exampleAnswers = selectQuestionAnswers(data, fixedRandomizeAnswers?.questionId);
    const { answersOrder: exampleAnswerOrder, answersIds: exampleAnswerIds } =
      findLastInStackSafe(answersStack, fixedRandomizeAnswers?.questionId) ?? {};

    const fixedRandomizeResult = doFixedRandomizeAnswers({
      answers: originalAnswers,
      exampleAnswers,
      exampleAnswerIds,
      exampleAnswerOrder,
      getHiddenAnswers,
      excludedIds: randomizeAnswersExcludedIds,
    });

    /**
     * Если отсутствуют exampleAnswerOrder(ф-я doFixedRandomizeAnswers вернет null)
     * игнорируем фиксированную рандомизацию
     */
    if (fixedRandomizeResult) return fixedRandomizeResult;
  }

  const hiddenAnswers = getHiddenAnswers(originalAnswers, randomizeAnswers);
  const answers = hiddenAnswers.size > 0 ? originalAnswers.filter(it => !hiddenAnswers.has(it.id)) : originalAnswers;
  const answersIds = answers.map(it => it.id);

  let answersOrder: number[] = 'answers' in question.data ? range(originalAnswers.length) : [];

  /** Накатываем старые answersOrder на новые, например для POLL_RANK */
  const prevAnswerState = findLastInStackSafe(answersStack, question.id);
  const predefinedAnswerOrder = prevAnswerState?.answersOrder
    ?.reduce<string[]>((result, answerIndex, answerOrder) => {
      const answerId = prevAnswerState?.answersIds?.[answerIndex];

      if (answerId && answersIds.includes(answerId)) result[answerOrder] = answerId;
      return result;
    }, [])
    .filter(notNull);

  if (predefinedAnswerOrder) {
    predefinedAnswerOrder.forEach((answerId, predefinedIndex) => {
      const answerIndex = answers.findIndex(a => a.id === answerId);
      const prevIndex = answersOrder.findIndex(index => index === answerIndex);
      const prevValue = answersOrder[predefinedIndex];

      if (prevValue !== undefined) answersOrder[prevIndex] = prevValue;
      answersOrder[predefinedIndex] = answerIndex;
    });
  }

  if (randomizeAnswers) {
    if (groupId) {
      const currentGroupQuestionsInStateIds = getGroupQuestionsInStateIds(answersStack, data, groupId);
      const isQuestionInState = currentGroupQuestionsInStateIds.includes(question.id);
      const prevAnswerState = isQuestionInState ? findLastInStackSafe(answersStack, question.id) : null;

      const predefinedAnswerOrder = prevAnswerState?.answersOrder?.reduce<Record<string, number>>(
        (result, answerIndex, answerOrder) => {
          const answerId = prevAnswerState?.answersIds?.[answerIndex];
          return { ...result, ...(answerId && answersIds.includes(answerId) && { [answerId]: answerOrder }) };
        },
        {},
      );

      answersOrder = doRandomizeAnswers(answers, { predefinedAnswerOrder, excludedIds: randomizeAnswersExcludedIds });
    } else {
      answersOrder = doRandomizeAnswers(answers, { excludedIds: randomizeAnswersExcludedIds });
    }
  }

  if (question.data.type === 'POLL_MAXDIFF') {
    const screens = generateScreens(answers, question.data.answersOnScreen, question.data.numberOfScreens);

    answersOrder = screens
      .reduce((result, screen) => [...result, ...screen], [])
      .map(screen => answersIds.findIndex(answerId => answerId === screen.id));
  }

  return { answersIds, answersOrder };
}

function createDefaultAnsweredIds({
  question,
  answersOrder,
  template,
}: {
  question: Question;
  answersOrder: number[];
  template: string;
}) {
  if (question.data.type === 'POLL_RANK' && template === 'poll') {
    const { maximumRank, answers } = question.data;
    const orderedAnswers = answersOrder.length
      ? answersOrder
          .map(index => answers[index])
          .filter(notNull)
          .filter(answer => !answer.otherAnswer)
      : answers;
    return orderedAnswers.slice(0, maximumRank || orderedAnswers.length).map(({ id }) => id);
  }

  if (question.data.type === 'POLL_RATING_SLIDER' && question.data.preset === 'ipsative') {
    return ['2'];
  }

  if (question.data.type === 'POLL_SWITCH') {
    return ['0'];
  }

  return null;
}

export function createDefaultAnswerState(
  data: Poll,
  state: ICState,
  questionId: string,
): { defaultAnswerState: AnswerState; backStackItem: BackAnswerState | null } {
  const question = selectQuestion(data, questionId);
  if (!question) throw new Error(`Question with id ${questionId} not found`);

  const { startedAt, answersStack, backAnswerStack } = state;

  let isTimeExpired: undefined | 'changedSinceExpired';
  const now = new Date().getTime();

  /** Если глобальный таймер истек, то нужно проставить для всех новых вопросов isTimeExpired = true */
  if (data.hasTimer && answersStack.find(it => it.isTimeExpired)) {
    if (startedAt && data.timerDuration) {
      const isTimerExpired = getTimerDistance(startedAt, data.timerDuration) <= 0;
      if (isTimerExpired) {
        isTimeExpired = 'changedSinceExpired';
      }
    }
  }

  const { answersIds, answersOrder } = createAnswers({ data, question, state });
  const { backAnswerState, backStackItem } = getBackAnswerState(backAnswerStack, questionId, answersIds);
  const answeredIds = createDefaultAnsweredIds({ question, answersOrder, template: data.template });

  return {
    defaultAnswerState: {
      id: questionId,
      trackingId: nanoid(10),
      answersIds,
      answersOrder,
      isTimeExpired,
      createdAt: now,
      elapsedTime: NaN,
      stateId: nanoid(10),
      isNotApplicable: false,
      isAutoCommit: false,
      isSkipped: false,
      isWithoutAnswers: MEDIAFILE_TYPES.some(type => type === question.data.type),
      isAnswered: false,
      isCorrect: false,
      isStored: false,
      lastTouchedBy: null,
      ...(answeredIds && { answeredIds }),
      ...backAnswerState,
    },
    backStackItem,
  };
}

/** Если мы возвращаемся на страницу после перехода назад, то нужно отобразить ранее выбранные ответы */
function getBackAnswerState(backAnswerStack: BackAnswerState[], questionId: string, answersIds: string[] | undefined) {
  let backStackItem: BackAnswerState | null = null;
  /** Итерируемся в обратном порядке т.к новые вопросы лежат в конце стека */
  for (let i = backAnswerStack.length - 1; i >= 0; i--) {
    const item = backAnswerStack[i];
    if (item && !item.isUsed && item.id === questionId) {
      backStackItem = item;
      break;
    }
  }

  if (backStackItem) {
    /** Если список ответов не изменился предзаполняем ответы */
    if (isEqual(answersIds, backStackItem.answersIds)) {
      return {
        backAnswerState: pick(backStackItem, ['answeredIds', 'answeredOthers', 'answersOrder', 'lastTouchedBy']),
        backStackItem,
      };
    }
  }

  return { backAnswerState: {}, backStackItem };
}

export type AnswersMap = {
  [k: string]: AnswerState;
};

const computeHiddenAnswers = (() => {
  let hiddenAnswers = new Set<string>();
  let lastKey = '';

  return function (
    key: string,
    answers: Record<any, any>[] = [],
    {
      answersLimit,
      randomizeAnswers,
      randomizeAnswersExcludedIds,
    }: { answersLimit: number | null; randomizeAnswers: boolean; randomizeAnswersExcludedIds?: string[] },
    context: LogicConditionContext,
  ) {
    if (lastKey !== key) {
      lastKey = key;
      hiddenAnswers = new Set<string>();

      answers.forEach(it => {
        const { logicRules = [], logicOperationType = 'AND' } = it;
        if (logicRules.length > 0 && !isRulesApply(logicRules, logicOperationType, context)) {
          hiddenAnswers.add(it.id);
        }
      });
    }

    /** Показать ограниченное кол-во не системных ответов */
    if (typeof answersLimit === 'number' && answersLimit > 0) {
      answers = answers.filter(it => !hiddenAnswers.has(it.id) && !it.otherAnswer && !it.exclusiveAnswer);

      if (randomizeAnswers) {
        answers = shuffleAnswersWithExcluded(answers, randomizeAnswersExcludedIds);
      }
      /** Если количество не системных ответов больше answersLimit, необходимо убрать лишние ответы */
      if (answers.length > answersLimit) {
        answers.slice(answersLimit).forEach(it => hiddenAnswers.add(it.id));
      }
    }

    return hiddenAnswers;
  };
})();

export function createDefaultState(pollsIc?: string | null): ICState {
  return {
    welcomeId: null,
    questionId: null,
    groupId: null,
    finalId: null,
    pollsIc: pollsIc ?? uuid(),
    answersStack: [],
    trackingStack: [],
    backAnswerStack: [],
  };
}
